import { Building2 } from 'lucide-react';
import type { FilterState } from '../dashboardFilterUtils';
import { countryDisplayNames } from '../dashboardFilterUtils';
import SearchableSelect from '../../ui/SearchableSelect';
import { FilterSection, FilterGrid, FilterField, filterControlClass } from '../../filters/FilterSection';

interface CompanySectionProps {
  filters: FilterState;
  setFilters: (filters: FilterState) => void;
  divisions: Array<{ id: number; name: string }>;
  scopedDivisions: Array<{ id: number; name: string }>;
  isDivisionLocked: boolean;
  lockedDivisionValue: string;
  isSiteLocked: boolean;
  siteCountryOptions: string[];
  filteredSites: Array<{ id: number; name: string }>;
  filteredWarehouses: Array<{ id: number; name: string }>;
  /**
   * CR-4800 / CR-4802: warehouse is a logistics concept with no compliance
   * meaning, AND the filter is meaningless when the tenant has no warehouse
   * data at all — the caller combines both conditions.
   */
  showWarehouse: boolean;
  /**
   * CR-4802: hide Company Country when every accessible site is in the same
   * country — a single-country org has nothing for the filter to narrow.
   */
  showCountry: boolean;
  /** CR-4802: Division follows the tenant's declared/detected hierarchy shape. */
  showDivision?: boolean;
  /**
   * CR-6402: these are the customer-side hierarchy filters. On the customer
   * portal they describe the viewer's own organisation ("Company"); on the
   * supplier portal they describe the customers the supplier trades with
   * ("Customer"), which is what the supplier-view design labels them.
   */
  title: string;
}

/**
 * CR-6401: COMPANY group — the customer-side hierarchy filters, in the
 * diagram's order (Division, Country, Site, Warehouse). Division previously
 * sat alone in its own row, visually divorced from the Site it constrains.
 *
 * The Division -> Site and Country -> Site cascades are unchanged: both still
 * flow through the parent's handlers, this only moves where they render.
 */
export function CompanySection({
  filters,
  setFilters,
  divisions,
  scopedDivisions,
  isDivisionLocked,
  lockedDivisionValue,
  isSiteLocked,
  siteCountryOptions,
  filteredSites,
  filteredWarehouses,
  showWarehouse,
  showCountry,
  showDivision = true,
  title,
  onSiteCountryChange,
}: CompanySectionProps & { onSiteCountryChange: (value: string) => void }) {
  return (
    <FilterSection title={title} icon={Building2}>
      <FilterGrid>
        {/* CR-4349: Division only exists for tenants with an org hierarchy. */}
        {showDivision && divisions.length > 0 && (
          <FilterField label="Division">
            <select
              value={isDivisionLocked ? lockedDivisionValue : filters.division}
              onChange={(e) => setFilters({ ...filters, division: e.target.value })}
              disabled={isDivisionLocked}
              title={isDivisionLocked ? 'Locked by current view level' : undefined}
              className={`${filterControlClass} ${
                isDivisionLocked ? 'bg-muted/40 text-muted-foreground cursor-not-allowed' : ''
              }`}
            >
              {!isDivisionLocked && <option value="">All divisions</option>}
              {scopedDivisions.map((div) => (
                <option key={div.id} value={div.id}>{div.name}</option>
              ))}
            </select>
          </FilterField>
        )}

        {showCountry && (
          <FilterField label="Country">
            <select
              value={filters.siteCountry}
              onChange={(e) => onSiteCountryChange(e.target.value)}
              className={filterControlClass}
            >
              <option value="">All countries</option>
              {siteCountryOptions.map((code) => (
                <option key={code} value={code}>{countryDisplayNames[code] || code}</option>
              ))}
            </select>
          </FilterField>
        )}

        {/* CR-4577: locked to the single site the view level pins. */}
        <FilterField label="Site">
          {isSiteLocked ? (
            <div
              className="w-full px-3 py-2.5 border border-border rounded-md bg-muted/40 text-muted-foreground cursor-not-allowed"
              title="Locked by current view level"
            >
              {filteredSites[0]?.name || 'Selected site'}
            </div>
          ) : (
            <SearchableSelect
              options={filteredSites.map((s) => ({ value: String(s.id), label: s.name }))}
              value={filters.site}
              onChange={(val) => setFilters({ ...filters, site: val, warehouse: '' })}
              placeholder="All sites"
            />
          )}
        </FilterField>

        {showWarehouse && (
          <FilterField label="Warehouse">
            <select
              value={filters.warehouse}
              onChange={(e) => setFilters({ ...filters, warehouse: e.target.value })}
              className={filterControlClass}
            >
              <option value="">All warehouses</option>
              {filteredWarehouses.map((wh) => (
                <option key={wh.id} value={wh.id}>{wh.name}</option>
              ))}
            </select>
          </FilterField>
        )}
      </FilterGrid>
    </FilterSection>
  );
}
